import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';
import RecordRTC from 'recordrtc';
import { useNavigate } from 'react-router-dom';
import { useNavigationTracking } from '../../../hooks/useNavigationTracking';

interface FeedbackScreenProps {
  onNext: () => void;
  deferSave?: boolean;
  setFeedbackData?: (data: any) => void;
  navigateToProduct?: (
    productCode: string,
    preserveCustomerId?: boolean
  ) => void;
  autoSkipSeconds?: number;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  feedbackSessionId?: string;
}

export default function FeedbackScreen({
  onNext,
  deferSave = false,
  setFeedbackData,
  navigateToProduct,
  autoSkipSeconds = 0,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
  feedbackSessionId,
}: FeedbackScreenProps) {
  const {
    backendBaseUrl,
    resolveMediaUrl,
    product,
    style,
    userEmail,
    user,
    fetchData,
    sessionId,
    cycleCustomerId,
    setCycleCustomerId,
    isEventMode,
    consents,
    deviceData,
  } = useAppContext();
  const effectiveSessionId = feedbackSessionId || sessionId;
  const navigate = useNavigate();
  const { trackNavigation } = useNavigationTracking();
  const [localRecordingState, setLocalRecordingState] = useState<
    'initial' | 'recording'
  >('initial');
  const [recorder, setRecorder] = useState<any>(null);
  const [showTextReview, setShowTextReview] = useState<boolean>(false);
  const [textReview, setTextReview] = useState<string>('');
  const [isTextMode, setIsTextMode] = useState<boolean>(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [altroText, setAltroText] = useState<string>('');
  const [freeTextareaText, setFreeTextareaText] = useState<string>('');
  const [exceedMessage, setExceedMessage] = useState<string>('');
  const [isMultiDropdownOpen, setIsMultiDropdownOpen] =
    useState<boolean>(false);
  const multiDropdownRef = useRef<HTMLDivElement | null>(null);
  const autoSkipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [autoSkipDisabled, setAutoSkipDisabled] = useState<boolean>(false);
  const componentInstanceRef = useRef<number>(Date.now());
  const isProductMode = true;

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (!isMultiDropdownOpen) return;
      if (
        multiDropdownRef.current &&
        !multiDropdownRef.current.contains(e.target as Node)
      ) {
        setIsMultiDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMultiDropdownOpen]);

  useEffect(() => {
    (window as any).__hasSentFeedback = false;
    return () => {
      if (autoSkipTimerRef.current) {
        clearTimeout(autoSkipTimerRef.current);
        autoSkipTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (autoSkipTimerRef.current) {
      clearTimeout(autoSkipTimerRef.current);
      autoSkipTimerRef.current = null;
    }
    if (
      autoSkipSeconds > 0 &&
      !autoSkipDisabled &&
      localRecordingState === 'initial' &&
      !showTextReview &&
      !product?.multianswer_selection
    ) {
      autoSkipTimerRef.current = setTimeout(() => {
        onNext();
      }, autoSkipSeconds * 1000);
    }
    return () => {
      if (autoSkipTimerRef.current) {
        clearTimeout(autoSkipTimerRef.current);
        autoSkipTimerRef.current = null;
      }
    };
  }, [
    autoSkipSeconds,
    autoSkipDisabled,
    localRecordingState,
    showTextReview,
    product?.multianswer_selection,
    onNext,
    componentInstanceRef.current,
  ]);

  const getMicrophoneInput = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const newRecorder = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/webm',
        checkForInactiveTracks: true,
      });
      newRecorder.startRecording();
      setRecorder(newRecorder);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopMicrophoneInput = async (): Promise<Blob | null> => {
    if (recorder) {
      return new Promise(resolve => {
        recorder.stopRecording(() => {
          resolve(recorder.getBlob());
        });
      });
    }
    return null;
  };

  const sendReviewInBackground = (
    payload: object,
    localStorageText: string
  ) => {
    axios
      .post(`${backendBaseUrl}/send-to-insightgpt`, payload)
      .then(response => {
        localStorage.setItem('last_feedback_text', localStorageText);
        localStorage.setItem('last_feedback_rating', '0');
        if (response.data.customer_id) {
          setCycleCustomerId(response.data.customer_id);
        }
        if (response.data.feedback_id && deferSave && setFeedbackData) {
          setFeedbackData({
            textReview: localStorageText,
            isTextMode: true,
            microphoneInput: null,
            feedbackId: response.data.feedback_id,
          });
        }
      })
      .catch(error => {
        // Il save e' fire-and-forget: senza status e body qui, un feedback
        // rifiutato (es. 422 del gate consensi) e' indistinguibile da uno salvato.
        console.error(
          `Failed to send review: HTTP ${error?.response?.status ?? '?'}`,
          error?.response?.data ?? error?.message,
          {
            product: (payload as any)?.product?.code,
            customer_id: (payload as any)?.customer_id,
            consents: (payload as any)?.consents,
          }
        );
      });
  };

  const sendReview = (audioBlob?: Blob) => {
    if (isTextMode) {
      const payload: any = {
        product,
        text: textReview,
        userEmail,
        rating: 0,
        question: product?.product_indication,
        feedback_type: 'text',
        session_id: effectiveSessionId,
        customer_id: cycleCustomerId,
        consents,
        cycle_count: deviceData.cycle_count,
        codentify: deviceData.codentify,
        purchase_date: deviceData.purchase_date,
        adc: deviceData.adc,
        months: deviceData.months,
      };
      sendReviewInBackground(payload, textReview);
    } else if (audioBlob) {
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording_${Date.now()}.webm`);
      formData.append('rating', '0');
      axios
        .post(`${backendBaseUrl}/transcribe-audio`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(transcriptionResponse => {
          const transcribedText: string =
            transcriptionResponse.data.transcription;
          const payload: any = {
            product,
            text: transcribedText,
            userEmail,
            rating: 0,
            question: product?.product_indication,
            feedback_type: 'audio',
            session_id: effectiveSessionId,
            customer_id: cycleCustomerId,
            consents,
            cycle_count: deviceData.cycle_count,
            codentify: deviceData.codentify,
            purchase_date: deviceData.purchase_date,
          };
          sendReviewInBackground(payload, transcribedText);
        })
        .catch(error => {
          console.error(
            `Failed to transcribe/send audio review: HTTP ${error?.response?.status ?? '?'}`,
            error?.response?.data ?? error?.message
          );
        });
    }
  };

  const handleClick = async () => {
    if (!autoSkipDisabled) {
      setAutoSkipDisabled(true);
    }
    if (autoSkipTimerRef.current) {
      clearTimeout(autoSkipTimerRef.current);
      autoSkipTimerRef.current = null;
    }

    if (localRecordingState === 'initial') {
      setLocalRecordingState('recording');
      await getMicrophoneInput();
    } else if (localRecordingState === 'recording') {
      const blob = await stopMicrophoneInput();
      setLocalRecordingState('initial');
      sendReview(blob || undefined);
      onNext();
    }
  };

  const handleSendTextReview = () => {
    sendReview();
    onNext();
    setLocalRecordingState('initial');
    setShowTextReview(false);
    setTextReview('');
    setIsTextMode(false);
    setRecorder(null);
  };

  const handleSendMultiAnswer = () => {
    if (!autoSkipDisabled) {
      setAutoSkipDisabled(true);
    }
    if (autoSkipTimerRef.current) {
      clearTimeout(autoSkipTimerRef.current);
      autoSkipTimerRef.current = null;
    }

    const allowMultiple = Boolean(product?.multianswer_allow_multiple);
    const txOption = product?.multianswer_textarea_option || '';
    if (isTextareaOptionSelected && !altroText.trim()) return;
    const baseAnswersText = allowMultiple
      ? selectedAnswers
          .map(a => (txOption && a === txOption ? altroText.trim() : a))
          .join(' || ')
      : txOption && selectedAnswer === txOption
        ? altroText.trim()
        : selectedAnswer;
    const answersText =
      product?.multianswer_show_free_textarea && freeTextareaText.trim()
        ? baseAnswersText + ' || ' + freeTextareaText.trim()
        : baseAnswersText;
    if (allowMultiple ? selectedAnswers.length > 0 : Boolean(selectedAnswer)) {
      const payload: any = {
        product,
        text: answersText,
        userEmail,
        rating: 0,
        question: product?.product_indication,
        feedback_type: allowMultiple ? 'multi answer' : 'single answer',
        session_id: effectiveSessionId,
        customer_id: cycleCustomerId,
        consents,
        cycle_count: deviceData.cycle_count,
        codentify: deviceData.codentify,
        purchase_date: deviceData.purchase_date,
        adc: deviceData.adc,
        months: deviceData.months,
      };
      sendReviewInBackground(payload, answersText);
      onNext();
      setLocalRecordingState('initial');
      setSelectedAnswer('');
      setSelectedAnswers([]);
      setAltroText('');
      setFreeTextareaText('');
    }
  };

  const handleShowFiles = () => {
    if (
      user?.is_employee &&
      user?.employee_permissions?.includes('Feedbacks') &&
      product?.show_files
    ) {
      navigate('/employee', {
        state: {
          defaultEvent: product?.event_id,
          defaultProduct: product?.id,
        },
      });
    }
  };

  const handleSkipToProduct = () => {
    if (!product?.redirect_product) return;
    trackNavigation({
      from_screen: 'feedback',
      to_screen: 'skip_to_product',
      navigation_type: 'skip',
      screen_index: 0,
      total_screens: 1,
    });
    if (navigateToProduct) {
      navigateToProduct(
        product.redirect_product,
        isEventMode || Boolean(product.redirect_with_same_customer_id)
      );
    } else {
      navigate(`/?product=${product.redirect_product}`);
    }
  };

  const handleSkipNextScreen = () => {
    onNext();
  };

  const handleBackButton = () => {
    onBack?.();
  };

  const textareaOption = product?.multianswer_textarea_option || '';
  const isTextareaOptionSelected =
    Boolean(textareaOption) &&
    (product?.multianswer_allow_multiple
      ? selectedAnswers.includes(textareaOption)
      : selectedAnswer === textareaOption);
  const canSendMultiAnswer =
    (product?.multianswer_allow_multiple
      ? selectedAnswers.length > 0
      : Boolean(selectedAnswer)) &&
    (!isTextareaOptionSelected || altroText.trim().length > 0);

  const showSkipProduct = Boolean(
    product?.show_skip_product_button && product?.redirect_product
  );
  const showAnySkip =
    showSkipNextScreenButton || showSkipProduct || showBackButton;
  const skipBlockMarginTop = product?.multianswer_selection
    ? isTextMode
      ? 'clamp(16px, 2.5vh, 32px)'
      : 'clamp(20px, 4vh, 56px)'
    : isTextMode
      ? 'clamp(12px, 1.5vh, 20px)'
      : 'clamp(16px, 2.5vh, 32px)';

  return (
    <div
      style={{
        margin: '0 auto',
        position: 'relative',
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className="review-actions"
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          paddingBottom: '8px',
        }}
      >
        {product?.multianswer_selection ? (
          <div
            className="multi-answer-block"
            style={{
              height: 'auto',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginTop: '1vh',
            }}
          >
            <div
              className="yf-field"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {product?.multianswer_show_free_textarea && (
                <textarea
                  placeholder={
                    product?.multianswer_dropdown_indication || 'Scrivi qui...'
                  }
                  value={freeTextareaText}
                  onChange={e => setFreeTextareaText(e.target.value)}
                  style={{
                    width: '100%',
                    height: '8vh',
                    minHeight: '60px',
                    borderRadius: '7px',
                    padding: '8px 12px',
                    fontSize: '90%',
                    border: '1px solid #ccc',
                    marginBottom: '2vh',
                    background: '#f7f7f7',
                    boxSizing: 'border-box',
                    resize: 'none',
                  }}
                />
              )}
              {!product?.multianswer_allow_multiple && (
                <select
                  value={selectedAnswer}
                  onChange={e => {
                    if (!autoSkipDisabled) {
                      setAutoSkipDisabled(true);
                    }
                    if (autoSkipTimerRef.current) {
                      clearTimeout(autoSkipTimerRef.current);
                      autoSkipTimerRef.current = null;
                    }
                    setSelectedAnswer(e.target.value);
                  }}
                  style={{
                    width: '100%',
                    height: '4.6vh',
                    minHeight: '36px',
                    borderRadius: '7px',
                    padding: '0 20px',
                    fontSize: '90%',
                    border: '1px solid #ccc',
                    marginBottom: '2vh',
                    background: '#f7f7f7',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">
                    {product?.multianswer_dropdown_indication ||
                      'Select an answer...'}
                  </option>
                  {product?.multianswer_options?.map(
                    (option: string, index: number) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    )
                  )}
                  {textareaOption && (
                    <option value={textareaOption}>{textareaOption}</option>
                  )}
                </select>
              )}
              {product?.multianswer_allow_multiple && (
                <div
                  ref={multiDropdownRef}
                  style={{
                    width: '100%',
                    marginBottom: '2vh',
                    position: 'relative',
                  }}
                >
                  <div
                    onClick={() => {
                      if (!autoSkipDisabled) {
                        setAutoSkipDisabled(true);
                      }
                      if (autoSkipTimerRef.current) {
                        clearTimeout(autoSkipTimerRef.current);
                        autoSkipTimerRef.current = null;
                      }
                      setIsMultiDropdownOpen(prev => !prev);
                    }}
                    style={{
                      height: '4.6vh',
                      minHeight: '36px',
                      width: '100%',
                      borderRadius: '7px',
                      padding: '0 20px',
                      fontSize: '90%',
                      border: '1px solid #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      background: '#f7f7f7',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                    }}
                  >
                    <span
                      style={{
                        color: selectedAnswers.length ? '#07296A' : '#666',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {selectedAnswers.length
                        ? selectedAnswers.join(', ')
                        : product?.multianswer_dropdown_indication ||
                          'Select answers...'}
                    </span>
                    <span
                      style={{
                        transform: isMultiDropdownOpen
                          ? 'rotate(180deg)'
                          : 'none',
                        flexShrink: 0,
                        width: '12px',
                        textAlign: 'center',
                      }}
                    >
                      ▾
                    </span>
                  </div>
                  {isMultiDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        width: '100%',
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '7px',
                        padding: '10px 14px',
                        maxHeight: 'min(40vh, 320px)',
                        overflowY: 'auto',
                        zIndex: 5,
                        boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                      }}
                    >
                      {product?.multianswer_options?.map(
                        (option: string, index: number) => {
                          const checked = selectedAnswers.includes(option);
                          const max = Number(
                            product?.multianswer_max_answers || 0
                          );
                          const willExceed =
                            !checked &&
                            max > 0 &&
                            selectedAnswers.length >= max;
                          return (
                            <label
                              key={index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                margin: '6px 0',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={e => {
                                  if (e.target.checked) {
                                    if (willExceed) {
                                      setExceedMessage(
                                        product?.multianswer_exceed_limit_indication ||
                                          ''
                                      );
                                      setTimeout(
                                        () => setExceedMessage(''),
                                        2500
                                      );
                                      return;
                                    }
                                    setSelectedAnswers(prev => [
                                      ...prev,
                                      option,
                                    ]);
                                  } else {
                                    setSelectedAnswers(prev =>
                                      prev.filter(o => o !== option)
                                    );
                                  }
                                }}
                              />
                              <span>{option}</span>
                            </label>
                          );
                        }
                      )}
                      {textareaOption && (
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            margin: '6px 0',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAnswers.includes(textareaOption)}
                            onChange={e => {
                              if (e.target.checked) {
                                const max = Number(
                                  product?.multianswer_max_answers || 0
                                );
                                if (max > 0 && selectedAnswers.length >= max) {
                                  setExceedMessage(
                                    product?.multianswer_exceed_limit_indication ||
                                      ''
                                  );
                                  setTimeout(() => setExceedMessage(''), 2500);
                                  return;
                                }
                                setSelectedAnswers(prev => [
                                  ...prev,
                                  textareaOption,
                                ]);
                              } else {
                                setSelectedAnswers(prev =>
                                  prev.filter(o => o !== textareaOption)
                                );
                                setAltroText('');
                              }
                            }}
                          />
                          <span>{textareaOption}</span>
                        </label>
                      )}
                      {exceedMessage && (
                        <div
                          style={{
                            marginTop: '8px',
                            color: '#a00',
                            fontSize: '90%',
                          }}
                          dangerouslySetInnerHTML={{ __html: exceedMessage }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
              {isTextareaOptionSelected && (
                <textarea
                  placeholder="Scrivi la tua risposta..."
                  value={altroText}
                  onChange={e => setAltroText(e.target.value)}
                  style={{
                    width: '100%',
                    height: '8vh',
                    minHeight: '60px',
                    borderRadius: '7px',
                    padding: '8px 12px',
                    fontSize: '90%',
                    border: '1px solid #ccc',
                    marginBottom: '2vh',
                    background: '#f7f7f7',
                    boxSizing: 'border-box',
                    resize: 'none',
                  }}
                />
              )}
              <button
                disabled={!canSendMultiAnswer}
                style={
                  canSendMultiAnswer
                    ? {
                        height: '4.6vh',
                        minHeight: '36px',
                        width: '100%',
                        borderRadius: '7px',
                        padding: '0 20px',
                        fontSize: '90%',
                        fontWeight: 'bold',
                        color:
                          product?.multianswer_send_button_text_color ||
                          style?.text_review_button_text_color ||
                          'white',
                        background:
                          product?.multianswer_send_button_bg_color ||
                          style?.text_review_button_bg_color ||
                          'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow:
                          '0 5px 5px ' +
                          (product?.multianswer_send_button_shadow_color ||
                            style?.text_review_button_shadow_color ||
                            '#FFFFFF'),
                      }
                    : {
                        height: '4.6vh',
                        minHeight: '36px',
                        width: '100%',
                        borderRadius: '7px',
                        padding: '0 20px',
                        fontSize: '90%',
                        fontWeight: 'bold',
                        color:
                          product?.multianswer_send_button_text_color ||
                          style?.text_review_button_text_color ||
                          'white',
                        background:
                          product?.multianswer_send_button_bg_color ||
                          style?.text_review_button_bg_color ||
                          'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
                        border: 'none',
                        cursor: 'not-allowed',
                        boxShadow:
                          '0 5px 5px ' +
                          (product?.multianswer_send_button_shadow_color ||
                            style?.text_review_button_shadow_color ||
                            '#FFFFFF'),
                      }
                }
                onClick={handleSendMultiAnswer}
              >
                {product?.multianswer_send_button_indication || 'Send Answer'}
              </button>
            </div>
          </div>
        ) : !showTextReview ? (
          <>
            {product?.show_click_and_talk_button !== false && (
              <div
                id="audio-review"
                className="review-block"
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: 'clamp(16px, 2.5vh, 28px)',
                }}
              >
                {localRecordingState === 'initial' && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={resolveMediaUrl(style.recording_button_img)}
                      alt="Start Recording"
                      style={{
                        maxHeight: 'min(12vh, 30vw, 120px)',
                        maxWidth: 'min(12vh, 30vw, 120px)',
                        marginTop: 'clamp(16px, 2.5vh, 28px)',
                        cursor: 'pointer',
                      }}
                      onClick={handleClick}
                    />
                    <div
                      style={{
                        marginTop: '2vh',
                        fontWeight: 'bold',
                        fontSize: '100%',
                        color: '#07296A',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: style.recording_indication || '',
                      }}
                    />
                  </div>
                )}
                {localRecordingState === 'recording' && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={resolveMediaUrl(style.stoprecording_button_img)}
                      alt="Stop Recording"
                      style={{
                        maxHeight: 'min(12vh, 30vw, 120px)',
                        maxWidth: 'min(12vh, 30vw, 120px)',
                        marginTop: 'clamp(16px, 2.5vh, 28px)',
                        cursor: 'pointer',
                      }}
                      onClick={handleClick}
                    />
                    <div
                      style={{
                        marginTop: '10%',
                        fontWeight: 'bold',
                        fontSize: '100%',
                        color: '#84071E',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: style.stop_recording_indication || '',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {localRecordingState === 'initial' && (
              <div
                className="button-block"
                style={{
                  flex: '0 0 auto',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop:
                    product?.show_click_and_talk_button !== false
                      ? isProductMode
                        ? 'clamp(20px, 3.5vh, 40px)'
                        : '16px'
                      : 'clamp(16px, 2.5vh, 28px)',
                  marginBottom: 'clamp(16px, 2vh, 24px)',
                }}
              >
                <button
                  style={{
                    height: '4.6vh',
                    minHeight: '36px',
                    width: 'min(34vh, 90vw)',
                    maxWidth: '420px',
                    borderRadius: '7px',
                    padding: '0 20px',
                    fontSize: '90%',
                    fontWeight: 'bold',
                    color: style.text_review_button_text_color || 'white',
                    background:
                      style.text_review_button_bg_color ||
                      'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow:
                      '0 5px 5px ' +
                      (style.text_review_button_shadow_color || '#FFFFFF'),
                  }}
                  onClick={() => {
                    if (!autoSkipDisabled) {
                      setAutoSkipDisabled(true);
                    }
                    if (autoSkipTimerRef.current) {
                      clearTimeout(autoSkipTimerRef.current);
                      autoSkipTimerRef.current = null;
                    }
                    setShowTextReview(true);
                    setTextReview('');
                    setIsTextMode(true);
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      style.text_review_button_indication ||
                      'Write Text Review',
                  }}
                ></button>
              </div>
            )}
          </>
        ) : (
          <>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '1.2vh',
                marginTop: '1.6vh',
              }}
            >
              <textarea
                className="text-review-input"
                placeholder={
                  style?.text_review_placeholder_indication ||
                  'Write your text review here...'
                }
                value={textReview}
                onChange={e => setTextReview(e.target.value)}
                style={{
                  width: 'min(34vh, 90vw)',
                  maxWidth: '420px',
                  height: 'clamp(80px, 12vh, 160px)',
                  boxSizing: 'border-box',
                }}
              ></textarea>
              <button
                disabled={!textReview.trim()}
                style={{
                  height: 'clamp(36px, 4.6vh, 52px)',
                  width: 'min(34vh, 90vw)',
                  maxWidth: '420px',
                  borderRadius: '4px',
                  padding: '0 20px',
                  fontSize: '90%',
                  fontWeight: 'bold',
                  color: style.text_review_button_text_color
                    ? style.text_review_button_text_color || 'white'
                    : '#07296A',
                  background: style.text_review_button_bg_color
                    ? style.text_review_button_bg_color ||
                      'linear-gradient(to right, #6991F8 0%, #07296a 100%)'
                    : '#e4e5e9',
                  border: 'none',
                  cursor: textReview.trim() ? 'pointer' : 'not-allowed',
                  boxShadow: style.text_review_button_shadow_color
                    ? '0 5px 5px ' +
                      (style.text_review_button_shadow_color || '#FFFFFF')
                    : '0 5px 5px rgba(7, 41, 106, 0.5)',
                }}
                onClick={handleSendTextReview}
                dangerouslySetInnerHTML={{
                  __html:
                    style?.text_review_send_button_indication ||
                    'Send Text Review',
                }}
              ></button>
              <button
                onClick={() => {
                  setLocalRecordingState('initial');
                  setShowTextReview(false);
                  setTextReview('');
                  setIsTextMode(false);
                  setRecorder(null);
                }}
                style={{
                  fontSize: 'clamp(10px, 1.4vh, 14px)',
                  textDecoration: 'underline',
                  color: '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1.2,
                }}
                dangerouslySetInnerHTML={{
                  __html: style?.text_review_back_button_indication || 'Back',
                }}
              ></button>
            </div>
          </>
        )}
        {localRecordingState !== 'recording' && showAnySkip && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              marginTop: skipBlockMarginTop,
              flex: '0 0 auto',
            }}
          >
            {showBackButton && (
              <button
                type="button"
                onClick={handleBackButton}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.back_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: style?.back_button_indication || 'Back',
                  }}
                />
              </button>
            )}
            {showSkipNextScreenButton && (
              <button
                type="button"
                onClick={handleSkipNextScreen}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.skip_next_screen_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: style?.skip_next_screen_button_indication || 'Skip',
                  }}
                />
              </button>
            )}
            {showSkipProduct && (
              <button
                type="button"
                onClick={handleSkipToProduct}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.skip_product_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: showSkipNextScreenButton ? '0.8vh' : 0,
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      style?.skip_product_button_indication ||
                      'Skip to product',
                  }}
                />
              </button>
            )}
          </div>
        )}
        {user?.is_employee &&
          user?.employee_permissions?.includes('Feedbacks') &&
          product?.show_files && (
            <button
              onClick={handleShowFiles}
              style={{
                fontSize: '12px',
                textDecoration: 'underline',
                color: '#07296A',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                marginTop: '12px',
              }}
            >
              Show Files
            </button>
          )}
      </div>
    </div>
  );
}
